/**
 * The ProjectOnboarding component
 */
import { BowlineConnectForm } from "@bkry/bowline-components/BowlineConnectForm";
import { WalletProfileInputsForm } from "@bkry/bowline-components/WalletProfileInputsForm";
import {
  getProjectAttributes,
  getProjectRelationships,
} from "@bkry/bowline-redux/projects";
import {
  getWalletAttributes,
  registerProject,
  siweLogout,
  updateWallet,
} from "@bkry/bowline-redux/wallets";
import { getEnv } from "@bkry/bowline-utils";
import { useWeb3React } from "@web3-react/core";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Col,
  Container,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";

/**
 * Stateless react component to render the ProjectOnboarding component
 * @param {Object} props the props passed to the component.
 */
const ProjectOnboarding = (_props, context) => {
  const REACT_APP_PROJECT_ID = getEnv("REACT_APP_PROJECT_ID");
  const { account } = useWeb3React();
  const [currentAccount, setCurrentAccount] = useState(null);

  const dispatch = useDispatch();
  const signedInWithEthereum = useSelector(
    (state) => state.wallets.siweSuccess
  );

  useEffect(() => {
    if (
      (account && currentAccount !== account) ||
      (currentAccount && !account)
    ) {
      if (currentAccount) {
        dispatch(siweLogout());
      }
      setCurrentAccount(account);
    }
  }, [dispatch, siweLogout, account]);

  const projectData = useSelector((state) =>
    getProjectAttributes(state, REACT_APP_PROJECT_ID)
  );

  const projectRelationships = useSelector((state) =>
    getProjectRelationships(state, REACT_APP_PROJECT_ID)
  );

  const hasProfileInputs =
    projectRelationships?.wallet_profile_inputs?.data?.length > 0;

  const currentWalletId = useSelector((state) => state.wallets.currentWalletId);

  const currentWalletData = useSelector((state) =>
    getWalletAttributes(state, currentWalletId)
  );

  const [showWizzard, setShowWizzard] = useState(false);
  const [showProfileInputs, setShowProfileInputs] = useState(
    currentWalletData?.project_settings?.show_profile_completion || false
  );
  const [projectTosModal, setProjectTosModal] = useState(false);
  const toggleProjectTosModal = () => setProjectTosModal(!projectTosModal);
  const [projectDpModal, setProjectDpModal] = useState(false);
  const toggleProjectDpModal = () => setProjectDpModal(!projectDpModal);

  useEffect(() => {
    setShowWizzard(currentWalletData?.project_settings?.show_welcome);

    if (currentWalletData?.project_settings) {
      setShowProfileInputs(
        currentWalletData?.project_settings?.show_profile_completion
      );
    }
  }, [currentWalletData]);

  const dispatchRegisterProject = () => {
    dispatch(
      registerProject({
        projectId: REACT_APP_PROJECT_ID,
        address: currentWalletId,
        wallet: {
          project_tos_accepted: projectData?.terms_of_service ? true : null,
          project_dp_accepted: projectData?.data_privacy ? true : null,
        },
      })
    );
  };

  const handleProjectSubmission = (data) => {
    if (data?.userSharesEmail) {
      dispatch(
        updateWallet({
          projectId: REACT_APP_PROJECT_ID,
          address: currentWalletId,
          wallet: {
            email: data?.email,
          },
          callbackFnc: () => dispatchRegisterProject(),
        })
      );
    } else {
      dispatchRegisterProject();
    }

    setShowWizzard(false);
    setShowProfileInputs(true);
  };

  if (!currentWalletData) {
    return null;
  }

  return (
    <>
      <Modal
        isOpen={showWizzard && signedInWithEthereum}
        className="onboardingwizzard"
        fullscreen="md"
        size="xl"
        centered
        scrollable
      >
        <ModalBody className="pt-md-5 pb-md-5">
          <Container className="h-100">
            <Row className="h-100">
              <Col md={{ size: 7 }}>
                <h1>
                  👋{" "}
                  {context.t("Welcome to {project}", {
                    project: projectData?.title,
                  })}
                </h1>
                <div
                  dangerouslySetInnerHTML={{
                    __html: projectData?.welcome_text,
                  }}
                  className="mb-3"
                />
              </Col>
              <Col md={{ size: 5 }} className="d-flex flex-column">
                <BowlineConnectForm onSubmit={handleProjectSubmission} />
              </Col>
            </Row>
          </Container>
        </ModalBody>
        <ModalFooter>
          <p className="small">
            <>
              {(!!projectData?.terms_of_service ||
                !!projectData?.data_privacy) && (
                <>
                  {!!projectData?.terms_of_service && (
                    <>
                      {" "}
                      {context.t("By submitting you accept the")}{" "}
                      <Button
                        color="link"
                        size="sm"
                        onClick={toggleProjectTosModal}
                        className="p-0 pb-1"
                      >
                        {context.t("terms of service")}
                      </Button>
                      <Modal
                        isOpen={projectTosModal}
                        fullscreen="md"
                        size="xl"
                        centered
                        scrollable
                        toggle={toggleProjectTosModal}
                      >
                        <ModalHeader toggle={toggleProjectTosModal}>
                          {context.t("terms of service for {projectTitle}", {
                            projectTitle: projectData?.title,
                          })}
                        </ModalHeader>
                        <ModalBody>
                          <div
                            dangerouslySetInnerHTML={{
                              __html: projectData?.terms_of_service,
                            }}
                          />
                        </ModalBody>
                        <ModalFooter>
                          <Button
                            color="primary"
                            onClick={toggleProjectTosModal}
                          >
                            {context.t("Close")}
                          </Button>
                        </ModalFooter>
                      </Modal>
                    </>
                  )}
                  {!!projectData?.terms_of_service &&
                    !!projectData?.data_privacy && <> & </>}
                  {!!projectData?.data_privacy && (
                    <>
                      <Button
                        color="link"
                        size="sm"
                        onClick={toggleProjectDpModal}
                        className="p-0 pb-1"
                      >
                        {context.t("data protection policy")}
                      </Button>
                      <Modal
                        isOpen={projectDpModal}
                        fullscreen="md"
                        size="xl"
                        centered
                        scrollable
                        toggle={toggleProjectDpModal}
                      >
                        <ModalHeader toggle={toggleProjectDpModal}>
                          {context.t(
                            "data protection policy for {projectTitle}",
                            { projectTitle: projectData?.title }
                          )}
                        </ModalHeader>
                        <ModalBody>
                          <div
                            dangerouslySetInnerHTML={{
                              __html: projectData?.data_privacy,
                            }}
                          />
                        </ModalBody>
                        <ModalFooter>
                          <Button
                            color="primary"
                            onClick={toggleProjectDpModal}
                          >
                            {context.t("Close")}
                          </Button>
                        </ModalFooter>
                      </Modal>
                    </>
                  )}{" "}
                  {context.t("of {projectTitle}.", {
                    projectTitle: projectData?.title,
                  })}
                </>
              )}
            </>
            <div
              className="d-inline ms-1"
              dangerouslySetInnerHTML={{
                __html: context.t(
                  "For more information on the processing of your personal data through bowline, please see this <a href='https://www.bowline.app/data-protection/' target='_blank'>Privacy Policy</a>."
                ),
              }}
            />
          </p>
        </ModalFooter>
      </Modal>
      {signedInWithEthereum && hasProfileInputs && !showWizzard && (
        <WalletProfileInputsForm
          dismiss={() => setShowProfileInputs(false)}
          open={showProfileInputs}
        />
      )}
    </>
  );
};

/**  define proptype for the 'translation' function  */
ProjectOnboarding.contextTypes = {
  t: PropTypes.func,
};

ProjectOnboarding.propTypes = {};

export default ProjectOnboarding;
